/*
 * <copyright company="Argenbright Innovations Lab">
 *        copyright (c) Argenbright Innovations Lab, an Argenbright Holdings Company.  All rights reserved.
 * </copyright>
 */

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { AlertColor, Box, Button, CircularProgress, InputAdornment, TextField, Typography } from '@mui/material';
import { TimePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import { isEmpty } from 'lodash';

import { AdhocShiftsFormFields, DEFAULT_JOB_NUMBER_LENGTH, SnackbarSeverity } from '../../../../Shared/Constants/App';
import { getStoredCustomerDetails } from '../../../../Shared/Utilities/utils';

import AllJobsApiService from '../../Services/JobManagementService';
import { OrganizationService } from '../../../../Shared/Services/OrganizationService';

import DateSelection from '../../../../Shared/Components/Common/DatePicker/DateSelection';
import SnackbarMessage from '../../../../Shared/Components/Common/SnackbarMessage/SnackbarMessage';

import { DateFieldWrapper, ErrorMsg } from '../AddEditOneTimeJob/AddEditOneTimeJob.Style';
import { CardTitle, CloseDialogBoxIcon, buttonStyles } from '../UpdateShiftDate/UpdateShiftDate.Style';
import { DatePickerContainer } from '../JobDetails/OneTimeJobDetails.Style';
import theme from '../../../../Shared/Themes/theme';
import { Icons } from '../../../../Shared/Constants/Icons';
import { BoxShadow } from '../../../../Shared/Constants/Style.Constants';
import { DialogWrapper } from '../../../Vendor/Components/CreateBid.Style';

interface ICreateAdhocShift {
  openShiftCard: boolean;
  closeShiftCard: () => void;
  isLoading?: boolean;
  createAdhocShifts: (details: any) => void;
  selectedRows?: any[];
}

const CreateAdhocShiftCard = ({
  openShiftCard,
  closeShiftCard,
  isLoading,
  createAdhocShifts,
  selectedRows,
}: ICreateAdhocShift): JSX.Element => {
  const { t } = useTranslation(['altTexts', 'taskRejectionAndRework', 'rescheduleShiftDate, jobManagement']);

  const [isWorkTicketLoading, setIsWorkTicketLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<AlertColor>(SnackbarSeverity.success);
  const [jobNumberError, setJobNumberError] = useState<string>('');

  const [isEmployeeNumberLoading, setIsEmployeeNumber] = useState(false);
  const [employeeNumberError, setEmployeeNumberError] = useState<string>('');

  const { confirmButton, buttonText, textField } = buttonStyles;

  const { control, handleSubmit, watch, setValue, reset } = useForm({
    defaultValues: {
      [AdhocShiftsFormFields.JobNumber]: selectedRows?.[0]?.jobNumber || '',
      [AdhocShiftsFormFields.EmployeeNumber]: '',
      [AdhocShiftsFormFields.ScheduleDate]: null,
      [AdhocShiftsFormFields.StartTime]: '',
      [AdhocShiftsFormFields.EndTime]: '',
      [AdhocShiftsFormFields.WorkTicketNumber]: '',
      [AdhocShiftsFormFields.DaysExtendedBy]: '',
    },
  });

  const jobNumber = watch(AdhocShiftsFormFields.JobNumber);
  const employeeNumber = watch(AdhocShiftsFormFields.EmployeeNumber);
  const shiftDate = watch(AdhocShiftsFormFields.ScheduleDate);
  const startTime = watch(AdhocShiftsFormFields.StartTime);
  const endTime = watch(AdhocShiftsFormFields.EndTime);
  const daysExtendedBy = watch(AdhocShiftsFormFields.DaysExtendedBy);

  const { customerId = '' } = getStoredCustomerDetails() || {};

  const disableConfirmButton = () => {
    const baseDisabled = !jobNumber || !employeeNumber || !shiftDate || !startTime || !endTime;

    const hasDaysExtendedByError =
      endTime && startTime && endTime <= startTime && (!daysExtendedBy || Number(daysExtendedBy) < 1);

    return baseDisabled || hasDaysExtendedByError;
  };

  const showSnackbar = (severity: AlertColor, message: string) => {
    setSnackbarOpen(true);
    setSnackbarSeverity(severity);
    setSnackbarMessage(message);
  };

  const fetchWorkTicketNumber = async (jobNum: string) => {
    try {
      setIsWorkTicketLoading(true);
      const response = await AllJobsApiService.fetchWorkTickets(jobNum);

      if (response?.status && response?.status !== 200) {
        setIsWorkTicketLoading(false);
        showSnackbar(SnackbarSeverity.error, response?.data);
        setJobNumberError(response?.data);
        return;
      } else {
        setJobNumberError('');
      }
      setIsWorkTicketLoading(false);
    } catch (error) {
      console.log('error', error);
    }
  };

  const checkIfEmployeeExists = async (employeeNum: string) => {
    try {
      setIsEmployeeNumber(true);
      const response = await OrganizationService.getAllUsers(
        customerId,
        '',
        '',
        0,
        0,
        undefined,
        true,
        '',
        '',
        '',
        employeeNum
      );
      if (isEmpty(response?.data)) {
        setIsEmployeeNumber(false);
        showSnackbar(
          SnackbarSeverity.error,
          `${t('jobManagement:userWithEmployeeNumber')} ${employeeNum} ${t('jobManagement:doesNotExistsInPeazy')}`
        );
        setEmployeeNumberError(
          `${t('jobManagement:userWithEmployeeNumber')} ${employeeNum} ${t('jobManagement:doesNotExistsInPeazy')}`
        );
        return;
      } else {
        setEmployeeNumberError('');
      }
      setIsEmployeeNumber(false);
    } catch (error) {
      console.log('error', error);
    }
  };

  useEffect(() => {
    if (selectedRows?.[0]?.jobNumber) {
      setValue(AdhocShiftsFormFields.JobNumber, selectedRows?.[0]?.jobNumber);
    }
  }, [selectedRows, setValue]);

  useEffect(() => {
    if (openShiftCard) {
      if (jobNumber?.length >= DEFAULT_JOB_NUMBER_LENGTH) {
        fetchWorkTicketNumber(jobNumber);
      } else {
        setValue(AdhocShiftsFormFields.WorkTicketNumber, '');
        setJobNumberError('');
      }
    }
  }, [jobNumber, openShiftCard]);

  useEffect(() => {
    if (openShiftCard) {
      if (employeeNumber?.length >= DEFAULT_JOB_NUMBER_LENGTH) {
        checkIfEmployeeExists(employeeNumber);
      } else {
        setEmployeeNumberError('');
      }
    }
  }, [employeeNumber, openShiftCard]);

  useEffect(() => {
    if (!openShiftCard) {
      reset({
        [AdhocShiftsFormFields.EmployeeNumber]: '',
        [AdhocShiftsFormFields.ScheduleDate]: null,
        [AdhocShiftsFormFields.StartTime]: '',
        [AdhocShiftsFormFields.EndTime]: '',
        [AdhocShiftsFormFields.WorkTicketNumber]: '',
        [AdhocShiftsFormFields.DaysExtendedBy]: '',
      });
    }
  }, [openShiftCard, reset]);

  useEffect(() => {
    if (!startTime || !endTime) return;

    const baseDate = shiftDate ? new Date(shiftDate) : new Date();
    const dateStr = baseDate?.toISOString()?.split('T')[0];

    const start = new Date(`${dateStr}T${startTime}:00`);
    const end = new Date(`${dateStr}T${endTime}:00`);

    if (end <= start && !daysExtendedBy) {
      setValue(AdhocShiftsFormFields.DaysExtendedBy, '1');
    } else if (!daysExtendedBy) {
      setValue(AdhocShiftsFormFields.DaysExtendedBy, '0');
    }
  }, [startTime, endTime, setValue]);

  return (
    <>
      <DialogWrapper
        open={openShiftCard}
        onClose={closeShiftCard}
        PaperProps={{
          style: { width: '50rem', maxWidth: '50rem' },
        }}>
        <CloseDialogBoxIcon onClick={closeShiftCard}>
          <img src={Icons.InfoWindowCloseIcon} alt={t('altTexts:closeIcon')} />
        </CloseDialogBoxIcon>
        <CardTitle>{t('jobManagement:createUnScheduleWork')}</CardTitle>
        <DatePickerContainer>
          <Controller
            control={control}
            name={AdhocShiftsFormFields.JobNumber}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <Box sx={{ width: '100%' }}>
                <TextField
                  fullWidth
                  label={
                    <span>
                      {t('vendor:jobNumber')}
                      <span className="mandatory-field">*</span>
                    </span>
                  }
                  type="text"
                  variant="outlined"
                  value={value}
                  onChange={(e) => {
                    onChange(e?.target?.value?.replace(/\D/g, ''));
                  }}
                  sx={{ ...buttonStyles.textField, width: '100%' }}
                  helperText={
                    error?.message
                      ? error?.message
                      : value && value?.length < DEFAULT_JOB_NUMBER_LENGTH
                        ? t('jobManagement:invalidJobNumber')
                        : !isEmpty(jobNumberError)
                          ? jobNumberError
                          : ''
                  }
                  FormHelperTextProps={{
                    sx: { color: 'error.main' },
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {isWorkTicketLoading ? (
                          <CircularProgress size={30} sx={{ color: theme.palette.primary.dark }} />
                        ) : (
                          value && (
                            <img
                              src={Icons.CancelIcon}
                              onClick={() => {
                                onChange('');
                              }}
                              style={{ cursor: 'pointer' }}
                            />
                          )
                        )}
                      </InputAdornment>
                    ),
                  }}
                  inputProps={{
                    inputMode: 'numeric',
                    pattern: '[0-9]*',
                  }}
                />
                <ErrorMsg style={{ minHeight: '1.2rem' }}>{error && error?.message}</ErrorMsg>
              </Box>
            )}
          />
          <Controller
            control={control}
            name={AdhocShiftsFormFields.EmployeeNumber}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <Box sx={{ width: '100%' }}>
                <TextField
                  fullWidth
                  label={
                    <span>
                      {t('jobManagement:employeeNumber')}
                      <span className="mandatory-field">*</span>
                    </span>
                  }
                  variant="outlined"
                  value={value}
                  onChange={(e) => {
                    onChange(e?.target?.value?.replace(/\D/g, ''));
                  }}
                  sx={{ ...buttonStyles.textField, width: '100%' }}
                  helperText={
                    error?.message
                      ? error?.message
                      : value && value?.length < DEFAULT_JOB_NUMBER_LENGTH
                        ? t('jobManagement:invalidJobNumber')
                        : !isEmpty(employeeNumberError)
                          ? employeeNumberError
                          : ''
                  }
                  FormHelperTextProps={{
                    sx: { color: 'error.main' },
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {isEmployeeNumberLoading ? (
                          <CircularProgress size={30} sx={{ color: theme.palette.primary.dark }} />
                        ) : (
                          value && (
                            <img
                              src={Icons.CancelIcon}
                              onClick={() => {
                                onChange('');
                              }}
                              style={{ cursor: 'pointer' }}
                            />
                          )
                        )}
                      </InputAdornment>
                    ),
                  }}
                />
                <ErrorMsg>{error && error?.message}</ErrorMsg>
              </Box>
            )}
          />
        </DatePickerContainer>
        <DatePickerContainer>
          <Box sx={{ width: '100%' }}>
            <DateSelection
              requiredText={t('calendar:dateFieldIsRequired')}
              control={control}
              fieldName={AdhocShiftsFormFields.ScheduleDate}
              label={t('jobManagement:scheduleDate')}
              isExcludePastDate={false}
              isExcludeFutureDate={false}
            />
          </Box>
          <Controller
            control={control}
            name={AdhocShiftsFormFields.DaysExtendedBy}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <Box sx={{ width: '100%' }}>
                <TextField
                  label={<span>{t('jobManagement:daysExtendedBy')}</span>}
                  variant="outlined"
                  fullWidth
                  value={value}
                  onChange={(e) => {
                    const numericValue = e?.target?.value?.replace(/\D/g, '');
                    if (numericValue === '' || Number(numericValue) <= 30) {
                      onChange(numericValue);
                    }
                  }}
                  sx={{
                    ...buttonStyles.textField,
                    width: '100%',
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {value && (
                          <img
                            src={Icons.CancelIcon}
                            onClick={() => {
                              onChange('');
                            }}
                            style={{ cursor: 'pointer' }}
                          />
                        )}
                      </InputAdornment>
                    ),
                  }}
                  helperText={
                    endTime && startTime && endTime <= startTime && (!daysExtendedBy || Number(daysExtendedBy) < 1)
                      ? t('jobManagement:overNightShiftExtended')
                      : error?.message
                  }
                  FormHelperTextProps={{
                    sx: { color: 'error.main' },
                  }}
                />
                <ErrorMsg>{error?.message}</ErrorMsg>
              </Box>
            )}
          />
        </DatePickerContainer>
        <DatePickerContainer>
          <Controller
            control={control}
            name={AdhocShiftsFormFields.StartTime}
            render={({ field: { onChange, value } }) => (
              <DateFieldWrapper>
                <TimePicker
                  label={
                    <span>
                      {t('calendar:startTime')}
                      <span className="mandatory-field">*</span>
                    </span>
                  }
                  value={value ? dayjs(value, 'HH:mm') : null}
                  onChange={(newValue) => {
                    const formatted = newValue ? newValue.format('HH:mm') : '';
                    onChange(formatted);
                  }}
                  slotProps={{
                    textField: { sx: textField },
                    popper: {
                      sx: {
                        '.MuiPaper-root': {
                          boxShadow: BoxShadow.BoxShadow,
                          '.MuiMultiSectionDigitalClockSection-item.Mui-selected': {
                            backgroundColor: theme.palette.primary.main,
                            fontWeight: theme.typography.fontWeightBold,
                          },
                          '.MuiMultiSectionDigitalClockSection-item:hover': {
                            backgroundColor: theme.palette.secondary.dark,
                          },
                          '.MuiButton-root': {
                            color: theme.palette.common.white,
                            backgroundColor: theme.palette.primary.dark,
                          },
                        },
                      },
                    },
                  }}
                />
              </DateFieldWrapper>
            )}
          />
          <Controller
            control={control}
            name={AdhocShiftsFormFields.EndTime}
            render={({ field: { onChange, value } }) => (
              <DateFieldWrapper>
                <TimePicker
                  label={
                    <span>
                      {t('calendar:endTime')}
                      <span className="mandatory-field">*</span>
                    </span>
                  }
                  value={value ? dayjs(value, 'HH:mm') : null}
                  onChange={(newValue) => {
                    const formatted = newValue ? newValue.format('HH:mm') : '';
                    onChange(formatted);
                  }}
                  slotProps={{
                    textField: { sx: textField },
                    popper: {
                      sx: {
                        '.MuiPaper-root': {
                          boxShadow: BoxShadow.BoxShadow,
                          '.MuiMultiSectionDigitalClockSection-item.Mui-selected': {
                            backgroundColor: theme.palette.primary.main,
                            fontWeight: theme.typography.fontWeightBold,
                          },
                          '.MuiMultiSectionDigitalClockSection-item:hover': {
                            backgroundColor: theme.palette.secondary.dark,
                          },
                          '.MuiButton-root': {
                            color: theme.palette.common.white,
                            backgroundColor: theme.palette.primary.dark,
                          },
                        },
                      },
                    },
                  }}
                />
              </DateFieldWrapper>
            )}
          />
        </DatePickerContainer>
        {/* Commenting isShiftBillable and WorkTickets dropdown to restrict unscheduleWork creation for OTJ's */}
        {/*
          {isWorkTicketLoading ? (
          <Loader>
            <CircularProgress size={30} sx={{ color: theme.palette.primary.dark }} />
          </Loader>
          ) : (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
              <IconButton sx={{ padding: 0, opacity: 0.8, cursor: 'not-allowed' }}>
                <img
                  src={isBillable ? Icons.CheckedIcon : Icons.Unchecked}
                  alt={isBillable ? 'checked' : 'unchecked'}
                  style={{ opacity: 0.8, cursor: 'not-allowed' }}
                />
              </IconButton>
              <span style={{ marginLeft: '0.5rem' }}>{t('jobManagement:IsShiftBillable')}</span>
            </Box>
            {isBillable && (
              <Controller
                control={control}
                name={AdhocShiftsFormFields.WorkTicketNumber}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <Box sx={{ width: '100%' }}>
                    <TextField
                      select
                      fullWidth
                      label={
                        <span>
                          {t('jobManagement:WorkTicketNumber')}
                          <span className="mandatory-field">*</span>
                        </span>
                      }
                      variant="outlined"
                      value={value}
                      onChange={(e) => {
                        onChange(e);
                      }}
                      sx={{ ...buttonStyles.textField, width: '100%' }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={() => setIsOpen?.(!isOpen)} style={{ padding: 0 }}>
                              <img
                                src={isOpen ? Icons.DropdownDownIcon : Icons.DropdownUpIcon}
                                alt={t?.(Labels.TextDropDown)}
                              />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      SelectProps={{
                        open: isOpen,
                        IconComponent: () => null,
                        onOpen: () => setIsOpen?.(true),
                        onClose: () => setIsOpen?.(false),
                        MenuProps: {
                          PaperProps: {
                            style: {
                              boxShadow: BoxShadow.BoxShadow,
                            },
                          },
                        },
                      }}>
                      {jobDetails?.map((item) => (
                        <Options key={item?.workTicketNumber} value={item?.workTicketNumber}>
                          {item?.workTicketNumber}
                        </Options>
                      ))}
                    </TextField>

                    <ErrorMsg>{error && error?.message}</ErrorMsg>
                  </Box>
                )}
              />
            )}
          </Box>
          )} 
        */}
        <Button
          sx={{
            ...confirmButton,
            opacity: disableConfirmButton() || isLoading ? 0.5 : 1,
          }}
          disabled={disableConfirmButton() || isLoading}
          onClick={handleSubmit(createAdhocShifts)}>
          {isLoading ? (
            <CircularProgress size={30} sx={{ color: theme.palette.common.white }} />
          ) : (
            <Typography
              sx={{
                ...buttonText,
                color: theme.palette.common.white,
              }}>
              {t('jobManagement:createUnScheduleWork')}
            </Typography>
          )}
        </Button>
      </DialogWrapper>
      <SnackbarMessage
        open={snackbarOpen}
        successMessage={snackbarMessage}
        errorMessage={snackbarMessage}
        severity={snackbarSeverity}
        onClose={() => setSnackbarOpen(false)}
      />
    </>
  );
};

export default CreateAdhocShiftCard;
